/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { ColorLike } from 'ol/colorlike';
import type { Coordinate } from 'ol/coordinate';
import type { FeatureLike } from 'ol/Feature';
import type {
  Geometry,
  MultiLineString,
  MultiPolygon,
  SimpleGeometry,
} from 'ol/geom';
import { LineString, MultiPoint, Polygon } from 'ol/geom';
import type {
  LineCoordType,
  GeometryFunction,
  SketchCoordType,
} from 'ol/interaction/Draw';
import { Circle, Fill, Icon, Stroke, Text } from 'ol/style';
import type { StyleFunction, StyleLike } from 'ol/style/Style';
import Style, { createDefaultStyle } from 'ol/style/Style';
import { locationPath, rotatePath } from '@opengeoweb/theme';
import type { DrawModeValue } from './draw/types';
import {
  FEATURE_DRAW_FILL_COLOR,
  FEATURE_DRAW_FILL_COLOR_TRANSPARENT,
  FEATURE_DRAW_STROKE_COLOR,
} from './OlColors';
import { createIconStyle } from './styles/useIconStyle';

export enum MapFeatureClass {
  MyLocation = 'MyLocation',
  GenericMarker = 'GenericMarker',
  ObservationStation = 'ObservationStation',
  EDRFeature = 'EDRFeature',
  EDRMultiPolygon = 'EDRMultiPolygon',
  default = 'default',
}

export const FEATURE_FILL = new Fill({
  color: FEATURE_DRAW_FILL_COLOR_TRANSPARENT,
});
export const FEATURE_STROKE = new Stroke({ color: '#e14', width: 1.5 });
export const FEATURE_STROKE_EDIT = new Stroke({
  color: FEATURE_DRAW_STROKE_COLOR,
  width: 1.5 + 1,
});
export const FEATURE_FILL_SELECTED = new Fill({
  color: FEATURE_DRAW_STROKE_COLOR,
});
export const FEATURE_STROKE_SELECTED = new Stroke({
  color: FEATURE_DRAW_STROKE_COLOR,
  width: 1.5 + 1,
});
export const FEATURE_VERTICE_IMAGE = new Circle({
  radius: 5,
  stroke: new Stroke({
    color: '#333',
    width: 1.5 + 1,
  }),
  fill: new Fill({
    color: '#fff',
  }),
});

export const FEATURE_VERTICE_HANDLE_IMAGE = new Circle({
  radius: 5,
  stroke: new Stroke({
    color: FEATURE_DRAW_STROKE_COLOR,
    width: 1.5 + 1,
  }),
  fill: new Fill({
    color: '#fff',
  }),
});

const ICON_LOCATIONMARKER = createIconStyle(locationPath, {
  render: {
    fillStyle: '#051039',
  },
  icon: {
    anchor: [0.5, 1],
  },
});

const ICON_LOCATIONMARKER_SELECTED = createIconStyle(locationPath, {
  render: {
    fillStyle: FEATURE_DRAW_STROKE_COLOR,
    strokeStyle: FEATURE_DRAW_STROKE_COLOR,
    lineWidth: 2,
  },
  icon: {
    anchor: [0.5, 1],
  },
});

const ICON_LOCATIONMARKER_MODIFY = createIconStyle(locationPath, {
  render: {
    fillStyle: '#eee',
    strokeStyle: FEATURE_DRAW_STROKE_COLOR,
    lineWidth: 2,
  },
  icon: {
    anchor: [0.5, 1],
  },
});

// Pre-rendered canvas for the rotate icon so we can create
// dynamically rotated instances that follow the box orientation.
const ROTATE_ICON_SIZE: [number, number] = [24, 24];
const ROTATE_ICON_CANVAS: HTMLCanvasElement | undefined =
  typeof document !== 'undefined'
    ? ((): HTMLCanvasElement | undefined => {
        const canvas = document.createElement('canvas');
        const [width, height] = ROTATE_ICON_SIZE;
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          return undefined;
        }
        const path = new Path2D(rotatePath);

        // Draw a white outline behind the rotate icon for better contrast
        ctx.lineWidth = 1.5;
        ctx.strokeStyle = 'rgba(255, 255, 255, 1)';
        ctx.stroke(path);

        // Fill the actual rotate icon
        ctx.fillStyle = '#051039';
        ctx.fill(path);
        return canvas;
      })()
    : undefined;

export const FEATURE_VERTICES_EDIT_HANDLES = new Style({
  image: FEATURE_VERTICE_IMAGE,
  geometry: (feature: FeatureLike): MultiPoint | undefined => {
    const geom = feature?.getGeometry();
    if (geom?.getType() === 'Polygon') {
      const coordinates = (geom as Polygon).getCoordinates()[0];
      return new MultiPoint(coordinates);
    }
    if (geom?.getType() === 'LineString') {
      const coordinates = (geom as LineString).getCoordinates();
      return new MultiPoint(coordinates);
    }
    return undefined;
  },
});

const FEATURE_OBSERVATIONSTATION = [
  // Outer circle
  new Style({
    image: new Circle({
      radius: 8,
      fill: new Fill({
        color: '#8888FF66',
      }),
    }),
  }),
  // Inner circle
  new Style({
    image: new Circle({
      radius: 2,
      fill: new Fill({ color: '#000' }),
    }),
  }),
];

const FEATURE_OBSERVATIONSTATION_HIDDEN = [
  // Outer circle
  new Style({
    image: new Circle({
      radius: 8,
      fill: new Fill({
        color: '#FFFFFF00',
      }),
    }),
  }),
];

const FEATURE_OBSERVATIONSTATION_HIDDEN_AND_HOVERED = [
  // Outer circle
  new Style({
    image: new Circle({
      radius: 8,
      fill: new Fill({
        color: '#8888FF66',
      }),
    }),
  }),
  // Inner circle
  new Style({
    image: new Circle({
      radius: 2,
      fill: new Fill({ color: '#000' }),
    }),
  }),
];

const FEATURE_OBSERVATIONSTATION_HOVER = [
  // Outer circle
  new Style({
    image: new Circle({
      radius: 8,
      fill: new Fill({
        color: '#8888FF66',
      }),
      stroke: new Stroke({
        color: '#000',
        width: 1,
      }),
    }),
  }),
  // Inner circle
  new Style({
    image: new Circle({
      radius: 4.5,
      fill: new Fill({ color: '#000' }),
    }),
  }),
];

const FEATURE_OBSERVATIONSTATION_SELECTED = [
  // Outer circle
  new Style({
    image: new Circle({
      radius: 8,
      fill: new Fill({
        color: '#FF0000',
      }),
      stroke: new Stroke({
        color: '#000',
        width: 1,
      }),
    }),
  }),
  // Inner circle
  new Style({
    image: new Circle({
      radius: 4.5,
      fill: new Fill({ color: '#000' }),
    }),
  }),
];

const FEATURE_OBSERVATIONSTATION_NEARBY = [
  // Outer circle
  new Style({
    image: new Circle({
      radius: 8,
      fill: new Fill({
        color: 'rgba(0, 255, 0, 0.8)',
      }),
      stroke: new Stroke({
        color: '#0000002d',
        width: 1,
      }),
    }),
  }),
  // Inner circle
  new Style({
    image: new Circle({
      radius: 4.5,
      fill: new Fill({ color: '#000' }),
    }),
  }),
];

const FEATURE_EDR = [
  new Style({
    fill: FEATURE_FILL,
    stroke: FEATURE_STROKE,
  }),
];

const FEATURE_EDR_HOVER = [
  new Style({
    fill: new Fill({ color: '#e14' }),
    stroke: new Stroke({ color: '#e14', width: 1.5 }),
  }),
];

const FEATURE_MY_LOCATION = new Style({
  renderer: (pixelCoordinates, state): void => {
    const { context: ctx, pixelRatio } = state;
    const coord = pixelCoordinates as number[];
    ctx.save();

    // Draw outer circle with shadow
    ctx.strokeStyle = '#fff';
    ctx.fillStyle = '#fff';
    ctx.shadowColor = '#00000080';
    ctx.shadowBlur = 10;
    ctx.beginPath();
    ctx.arc(coord[0], coord[1], 12 * pixelRatio, 0, 2 * Math.PI);
    ctx.fill();
    ctx.shadowBlur = 0;

    // Draw inner circle
    ctx.fillStyle = '#186dff';
    ctx.beginPath();
    ctx.arc(coord[0], coord[1], 9 * pixelRatio, 0, 2 * Math.PI);
    ctx.fill();

    // Reset transformations
    ctx.restore();
  },
});

export const getFeaturePointStyle = (
  fillColor?: string,
  fillOpacity?: number,
): Style => {
  return new Style({
    renderer: (pixelCoordinates, state): void => {
      const { context: ctx, pixelRatio } = state;
      const coord = pixelCoordinates as number[];
      ctx.save();

      // Use provided fillColor and fillOpacity, or fallback to default
      ctx.fillStyle = fillColor
        ? geowebColorToOpenLayersColor(fillColor, fillOpacity || 1)
        : FEATURE_DRAW_FILL_COLOR;
      ctx.shadowColor = '#fff';
      ctx.shadowBlur = 5;
      ctx.beginPath();
      ctx.arc(coord[0], coord[1], 9 * pixelRatio, 0, 2 * Math.PI);
      ctx.fill();
      ctx.shadowBlur = 0;

      ctx.restore();
    },
  });
};

const FEATURE_POINT_MODIFY = new Style({
  renderer: (pixelCoordinates, state): void => {
    const { context: ctx, pixelRatio } = state;
    const coord = pixelCoordinates as number[];
    ctx.save();

    ctx.fillStyle = FEATURE_DRAW_FILL_COLOR_TRANSPARENT;
    ctx.beginPath();
    ctx.arc(coord[0], coord[1], 9 * pixelRatio, 0, 2 * Math.PI);
    ctx.fill();
    ctx.strokeStyle = FEATURE_DRAW_STROKE_COLOR;
    ctx.lineWidth = 3;
    ctx.shadowColor = '#fff';
    ctx.shadowBlur = 5;
    ctx.stroke();
    ctx.shadowBlur = 0;
    ctx.restore();
  },
});

export const labelOptions = {
  font: '16px Roboto',
  padding: [8, 8, 8, 8],
  fill: new Fill({ color: '#eee' }),
  text: '-', // f.getProperties().name,
  backgroundFill: new Fill({ color: '#111' }),
  backgroundStroke: new Stroke({
    color: '#111',
    width: 4,
    lineCap: 'round',
    lineJoin: 'round',
  }),
  offsetY: 40,
  overflow: true,
};

const labelBaseStyle = new Style({
  text: new Text(labelOptions),
});

// Multi-polygons need separate handling to only draw single label
const multiPolygonLabelBaseStyle = new Style({
  text: new Text(labelOptions),
  geometry: (feature): Geometry => {
    const polygons = (feature.getGeometry() as MultiPolygon).getPolygons();
    const largestPolygon = polygons.reduce((prev, curr) =>
      prev.getArea() > curr.getArea() ? prev : curr,
    );
    return largestPolygon.getInteriorPoint();
  },
});

// MultiLineStrings need separate handling to only draw single label
const multiLineStringLabelBaseStyle = new Style({
  text: new Text(labelOptions),
  geometry: (feature): Geometry => {
    const lines = (feature.getGeometry() as MultiLineString).getLineStrings();
    const longestLine = lines.reduce((prev, curr) =>
      prev.getLength() > curr.getLength() ? prev : curr,
    );
    return longestLine;
  },
});

export const textLabelStyle = (text: string): Style => {
  labelBaseStyle.getText()?.setText(text);
  return labelBaseStyle;
};

export const multiPolygonLabelStyle = (text: string): Style => {
  multiPolygonLabelBaseStyle.getText()?.setText(text);
  return multiPolygonLabelBaseStyle;
};

export const multiLineStringLabelStyle = (text: string): Style => {
  multiLineStringLabelBaseStyle.getText()?.setText(text);
  return multiLineStringLabelBaseStyle;
};

export const measureToolStyle = (lineColor: string): Style => {
  // Sketch style while drawing: dashed line + endpoint circle both with white halo to be visible on colored backgrounds.
  return new Style({
    renderer: (pixelCoordinates, state): void => {
      const { context: ctx, pixelRatio } = state;
      const raw = pixelCoordinates as number[] | number[][];

      const coords: number[] = Array.isArray(raw[0])
        ? (raw as number[][]).flat()
        : (raw as number[]);

      if (!coords || coords.length < 2) {
        return;
      }

      ctx.save();
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';

      const dashLength = 10 * pixelRatio;
      const dashPattern = [dashLength, dashLength];

      const drawLine = (width: number, color: string): void => {
        if (coords.length < 4) {
          return;
        }
        ctx.beginPath();
        ctx.setLineDash(dashPattern);
        ctx.lineWidth = width;
        ctx.strokeStyle = color;

        ctx.moveTo(coords[0], coords[1]);
        for (let i = 2; i < coords.length; i += 2) {
          ctx.lineTo(coords[i], coords[i + 1]);
        }
        ctx.stroke();
      };

      const mainLineWidth = 2 * pixelRatio;
      const shadowLineWidth = 2.5 * pixelRatio;
      drawLine(shadowLineWidth, 'rgba(255, 255, 255, 0.5)');
      drawLine(mainLineWidth, lineColor);

      const lastX = coords[coords.length - 2];
      const lastY = coords[coords.length - 1];

      const baseRadius = 8 * pixelRatio;

      ctx.beginPath();
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.5)';
      ctx.lineWidth = 2.5 * pixelRatio;
      ctx.arc(lastX, lastY, baseRadius, 0, 2 * Math.PI);
      ctx.stroke();

      ctx.beginPath();
      ctx.fillStyle = 'transparent';
      ctx.strokeStyle = FEATURE_DRAW_STROKE_COLOR;
      ctx.lineWidth = 2 * pixelRatio;
      ctx.arc(lastX, lastY, baseRadius, 0, 2 * Math.PI);
      ctx.fill();
      ctx.stroke();

      ctx.restore();
    },
  });
};

export const measureResultStyle = (lineColor: string): Style => {
  // Final result style: solid line with white halo to be visible on colored backgrounds.
  return new Style({
    renderer: (pixelCoordinates, state): void => {
      const { context: ctx, pixelRatio } = state;
      const raw = pixelCoordinates as number[] | number[][];

      const coords: number[] = Array.isArray(raw[0])
        ? (raw as number[][]).flat()
        : (raw as number[]);

      if (!coords || coords.length < 2) {
        return;
      }

      ctx.save();
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';

      const drawLine = (width: number, color: string): void => {
        if (coords.length < 4) {
          return;
        }
        ctx.beginPath();
        ctx.setLineDash([]);
        ctx.lineWidth = width;
        ctx.strokeStyle = color;

        ctx.moveTo(coords[0], coords[1]);
        for (let i = 2; i < coords.length; i += 2) {
          ctx.lineTo(coords[i], coords[i + 1]);
        }
        ctx.stroke();
      };

      const mainLineWidth = 2 * pixelRatio;
      const shadowLineWidth = 2.5 * pixelRatio;
      drawLine(shadowLineWidth, 'rgba(255, 255, 255, 0.5)');
      drawLine(mainLineWidth, lineColor);

      ctx.restore();
    },
  });
};

const stylesByMapFeatureClass: Record<
  MapFeatureClass,
  Style | Style[] | StyleFunction
> = {
  [MapFeatureClass.GenericMarker]: (feature: FeatureLike) =>
    !feature.get('hover')
      ? ICON_LOCATIONMARKER
      : [ICON_LOCATIONMARKER, textLabelStyle(feature.getProperties().name)],
  [MapFeatureClass.MyLocation]: (feature: FeatureLike) =>
    !feature.get('hover')
      ? FEATURE_MY_LOCATION
      : [FEATURE_MY_LOCATION, textLabelStyle(feature.getProperties().name)],
  [MapFeatureClass.ObservationStation]: (feature: FeatureLike) => {
    if (
      feature.get('hidden') &&
      feature.get('hover') &&
      feature.get('highlightSelectedFeature')
    ) {
      return [
        ...FEATURE_OBSERVATIONSTATION_SELECTED,
        textLabelStyle(feature.getProperties().name),
      ];
    }
    if (feature.get('highlightNearbyFeature')) {
      return [
        ...FEATURE_OBSERVATIONSTATION_NEARBY,
        textLabelStyle(feature.getProperties().name),
      ];
    }
    if (feature.get('hidden') && feature.get('hover')) {
      return [
        ...FEATURE_OBSERVATIONSTATION_HIDDEN_AND_HOVERED,
        textLabelStyle(feature.getProperties().name),
      ];
    }
    if (feature.get('highlightSelectedFeature') && feature.get('hover')) {
      return [
        ...FEATURE_OBSERVATIONSTATION_SELECTED,
        textLabelStyle(feature.getProperties().name),
      ];
    }
    if (feature.get('highlightSelectedFeature')) {
      return FEATURE_OBSERVATIONSTATION_SELECTED;
    }
    if (feature.get('hidden')) {
      return FEATURE_OBSERVATIONSTATION_HIDDEN;
    }
    if (feature.get('hover')) {
      return [
        ...FEATURE_OBSERVATIONSTATION_HOVER,
        textLabelStyle(feature.getProperties().name),
      ];
    }
    return FEATURE_OBSERVATIONSTATION;
  },

  [MapFeatureClass.EDRFeature]: (feature: FeatureLike) =>
    !feature.get('hover')
      ? FEATURE_EDR
      : [...FEATURE_EDR_HOVER, textLabelStyle(feature.getProperties().name)],
  [MapFeatureClass.EDRMultiPolygon]: (feature: FeatureLike) =>
    !feature.get('hover')
      ? FEATURE_EDR
      : [
          ...FEATURE_EDR_HOVER,
          multiPolygonLabelStyle(feature.getProperties().name),
        ],
  [MapFeatureClass.default]: (feature: FeatureLike) => {
    if (feature.get('highlightSelectedFeature')) {
      return [
        // Polygon / LineString
        new Style({
          fill: FEATURE_FILL_SELECTED,
          stroke: FEATURE_STROKE_SELECTED,
        }),
        // Point
        ICON_LOCATIONMARKER_SELECTED,
      ];
    }
    return [
      // Polygon / LineString
      new Style({
        fill: FEATURE_FILL,
        stroke: FEATURE_STROKE,
      }),
      // Point
      ICON_LOCATIONMARKER,
    ];
  },
};

export const geowebColorToOpenLayersColor = (
  color: string,
  opacity?: string | number,
): ColorLike => {
  // Return as-is if no opacity, or non-hex color (we do not know how to add opacity to non-hex colors)
  if (!color.startsWith('#') || opacity === undefined) {
    return color;
  }

  const decimalToHex = (decimal: number, padding: string): string => {
    const hex = decimal.toString(16).split('');

    if (hex.length < padding.length) {
      hex.push(padding.substring(hex.length));
    }

    return hex.join('');
  };

  const opacityNumber = Math.min(
    Math.max(0, Number.parseFloat(`${opacity}`)),
    255 / 256,
  );

  const bitsPerColor = color.length - 1 >= 6 ? 8 : 4;

  const resultingColor = color.split('');

  // Remove existing opacity bits if set
  if (bitsPerColor === 8 && resultingColor.length > 6 + 1) {
    resultingColor.splice(6 + 1);
  } else if (bitsPerColor === 4 && resultingColor.length > 3 + 1) {
    resultingColor.splice(3 + 1);
  }

  if (bitsPerColor === 8) {
    resultingColor.push(decimalToHex(Math.floor(256 * opacityNumber), '00'));
  } else {
    resultingColor.push(Math.floor(16 * opacityNumber).toString(16));
  }

  return resultingColor.join('');
};

export const inlineFeatureStyle = (feature: FeatureLike): Style[] => {
  const fillProperty = feature.getProperties()['fill'];
  const strokeProperty = feature.getProperties()['stroke'];
  const fillStrokeStyle = new Style();

  const styles: Style[] = [fillStrokeStyle];

  if (fillProperty) {
    const fillColor = geowebColorToOpenLayersColor(
      fillProperty,
      feature.getProperties()['fill-opacity'],
    );
    fillStrokeStyle.setFill(
      new Fill({
        color: fillColor,
      }),
    );

    const iconStyle = createIconStyle(locationPath, {
      render: {
        fillStyle: fillColor,
      },
      icon: {
        anchor: [0.5, 1],
      },
    });
    styles.push(iconStyle);
  }
  if (strokeProperty) {
    fillStrokeStyle.setStroke(
      new Stroke({
        color: geowebColorToOpenLayersColor(
          strokeProperty,
          feature.getProperties()['stroke-opacity'],
        ),
        width: feature.getProperties()['stroke-width'],
      }),
    );
  }

  return styles;
};

export const genericOpenLayersFeatureStyle: StyleFunction = (
  feature: FeatureLike,
  resolution: number,
) => {
  const props = feature.getProperties();
  const {
    fill,
    'fill-opacity': fillOpacity,
    stroke,
    'stroke-opacity': strokeOpacity,
    'stroke-width': strokeWidth,
    hover,
    mapFeatureClass,
    showNameOnHover,
    showUntilHoverOut,
    name,
    geometry,
    selectionType,
  } = props;

  // Features with showUntilHoverOut are visible initially (hover === undefined)
  // and when hovered (hover === true), but hidden after user hovers out (hover === false)
  if (showUntilHoverOut) {
    if (hover === false) {
      // User has hovered and moved out - make nearly invisible but still hoverable
      return new Style({
        stroke: new Stroke({
          color: 'rgba(0, 0, 0, 0.001)',
          width: strokeWidth || 1,
        }),
        fill: new Fill({
          color: 'rgba(0, 0, 0, 0.001)',
        }),
      });
    }
    // Initial state (hover undefined) or currently hovering (hover true) - show styling
    return new Style({
      stroke: new Stroke({
        color: geowebColorToOpenLayersColor(stroke, strokeOpacity || 0.8),
        width: strokeWidth || 1.2,
      }),
      fill: new Fill({
        color: geowebColorToOpenLayersColor(fill, fillOpacity || 0.1),
      }),
      zIndex: 9998,
    });
  }

  if (selectionType === 'smooth-line') {
    const geom = feature.getGeometry();
    if (geom instanceof LineString) {
      const coords = geom.getCoordinates();
      const smoothed = catmullRomSpline(coords, 16);

      // Smoothed line
      return new Style({
        geometry: new LineString(smoothed),
        stroke: new Stroke({
          width: strokeWidth || 3,
          color: geowebColorToOpenLayersColor(stroke, strokeOpacity || 1),
        }),
      });
    }
    return undefined;
  }

  const shouldHighlight =
    geometry &&
    geometry.getType() !== 'Point' &&
    mapFeatureClass !== MapFeatureClass.ObservationStation &&
    !showNameOnHover &&
    selectionType !== 'fir';

  // Highlight map polygons on hover
  if (hover && shouldHighlight) {
    return new Style({
      stroke: new Stroke({
        color: stroke || '#E27D00',
        width: 3,
      }),
      fill: new Fill({
        color: fill
          ? geowebColorToOpenLayersColor(fill, fillOpacity || 0.9)
          : 'rgba(226, 125, 0, 0.6)',
      }),
      zIndex: 9999,
    });
  }

  if (hover && showNameOnHover && (fill || stroke)) {
    const type = geometry.getType();
    switch (type) {
      case 'MultiPolygon':
        return [...inlineFeatureStyle(feature), multiPolygonLabelStyle(name)];
      case 'MultiLineString':
        return [
          ...inlineFeatureStyle(feature),
          multiLineStringLabelStyle(name),
        ];
      default:
        return [...inlineFeatureStyle(feature), textLabelStyle(name)];
    }
  }

  if (
    (!mapFeatureClass || mapFeatureClass === MapFeatureClass.default) &&
    geometry &&
    selectionType === 'point'
  ) {
    return getFeaturePointStyle(fill, fillOpacity);
  }

  if (fill || stroke) {
    return inlineFeatureStyle(feature);
  }

  const styleOrFunction =
    stylesByMapFeatureClass[
      (mapFeatureClass as MapFeatureClass) || MapFeatureClass.default
    ];

  return typeof styleOrFunction === 'function'
    ? styleOrFunction(feature, resolution)
    : styleOrFunction;
};

// Used for polygons, boxes and linestrings
const drawPolygonLikeFeatureStyle: StyleLike = (feature: FeatureLike) => {
  if (feature.getGeometry()?.getType() === 'Polygon') {
    return [new Style({ fill: FEATURE_FILL })];
  }

  // LineStrings, this includes Polygons being drawn (OL Draw has them as LineStrings until the Polygon is ready)
  return [
    // Style for the line
    new Style({ stroke: FEATURE_STROKE_EDIT }),
    // Extract vertices as points
    new Style({
      image: FEATURE_VERTICE_IMAGE,
      geometry: (feature: FeatureLike): MultiPoint | undefined => {
        const geom = feature?.getGeometry();
        if (geom?.getType() === 'LineString') {
          const coordinates = (geom as LineString).getCoordinates();
          return new MultiPoint(coordinates);
        }
        return undefined;
      },
    }),
  ];
};

// Simple Catmull-Rom spline smoothing
export const catmullRomSpline = (
  points: LineCoordType,
  numPoints = 16,
): Coordinate[] => {
  const result: Coordinate[] = [];
  if (points.length < 2) {
    return points;
  }

  for (let i = 0; i < points.length - 1; i += 1) {
    const p0 = points[i - 1] ?? points[i];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[i + 2] ?? p2;

    // always include the control point
    result.push(p1);

    for (let pointIndex = 1; pointIndex < numPoints; pointIndex += 1) {
      const t = pointIndex / numPoints;
      const t2 = t * t;
      const t3 = t2 * t;

      const x =
        0.5 *
        (2 * p1[0] +
          (-p0[0] + p2[0]) * t +
          (2 * p0[0] - 5 * p1[0] + 4 * p2[0] - p3[0]) * t2 +
          (-p0[0] + 3 * p1[0] - 3 * p2[0] + p3[0]) * t3);

      const y =
        0.5 *
        (2 * p1[1] +
          (-p0[1] + p2[1]) * t +
          (2 * p0[1] - 5 * p1[1] + 4 * p2[1] - p3[1]) * t2 +
          (-p0[1] + 3 * p1[1] - 3 * p2[1] + p3[1]) * t3);

      result.push([x, y]);
    }
  }

  // include the very last control point
  result.push(points[points.length - 1]);

  return result;
};

const smoothLineLikeFeatureStyle: StyleLike = (feature: FeatureLike) => {
  const props = feature.getProperties();
  const {
    stroke,
    'stroke-opacity': strokeOpacity,
    'stroke-width': strokeWidth,
  } = props;

  const geom = feature.getGeometry();
  if (geom instanceof LineString) {
    const coords = geom.getCoordinates();
    const smoothed = catmullRomSpline(coords, 16);
    return [
      // Smoothed line
      new Style({
        geometry: new LineString(smoothed),
        stroke: new Stroke({
          width: strokeWidth || 2,
          color: geowebColorToOpenLayersColor(
            stroke || FEATURE_DRAW_STROKE_COLOR,
            strokeOpacity || 1,
          ),
        }),
      }),
      // Vertices
      FEATURE_VERTICES_EDIT_HANDLES,
    ];
  }
  return undefined;
};

const pointFeatureStyle: StyleLike = getFeaturePointStyle();
const modifyPointFeatureStyle: StyleLike = FEATURE_POINT_MODIFY;
const locationFeatureStyle: StyleLike = ICON_LOCATIONMARKER_MODIFY;

const freehandLineFeatureStyle: StyleLike = [
  new Style({ stroke: FEATURE_STROKE_EDIT }),
];

const modifyPolygonLikeFeatureStyle: StyleLike = [
  // Area
  new Style({
    fill: FEATURE_FILL,
    stroke: FEATURE_STROKE_EDIT,
  }),
  // Vertices
  FEATURE_VERTICES_EDIT_HANDLES,
];

// This function creates a box out of a modified polygon. It detects which corner was moved by comparing vertices between the two geometries. Then it draws a new box based on the moved vertice and the opposing vertice.
// For squares the box scales uniformly around the fixed center.
// For rectangles the diagonal opposite corner does not change, the other two corners are recomputed.
// The original rotation is preserved.
export const createBoxBasedOnModifiedVertex = (
  modifiedGeometry: Polygon,
  originalGeometry: Polygon,
  keepSquare = false,
): Polygon => {
  const originalRing = originalGeometry.getCoordinates()[0];
  const modifiedRing = modifiedGeometry.getCoordinates()[0];

  if (!originalRing || !modifiedRing || originalRing.length < 4) {
    return originalGeometry;
  }

  // Strip closing coordinate if present so we only work with the four corners.
  const stripClose = (ring: Coordinate[]): Coordinate[] => {
    if (
      ring.length > 4 &&
      ring[0][0] === ring[ring.length - 1][0] &&
      ring[0][1] === ring[ring.length - 1][1]
    ) {
      return ring.slice(0, ring.length - 1);
    }
    return ring;
  };

  const originalCorners = stripClose(originalRing);
  const modifiedCorners = stripClose(modifiedRing);

  if (originalCorners.length < 4 || modifiedCorners.length < 4) {
    return originalGeometry;
  }

  // Detect which corner was dragged by comparing original vs modified
  const draggedCornerIndex = originalCorners.findIndex((orig, idx) => {
    const mod = modifiedCorners[idx];
    return orig[0] !== mod[0] || orig[1] !== mod[1];
  });

  if (draggedCornerIndex === -1) {
    // No corner actually changed, keep original
    return originalGeometry;
  }

  const dragged = modifiedCorners[draggedCornerIndex];

  // Compute center of the original box
  const centerX =
    originalCorners.reduce((sum, [x]) => sum + x, 0) / originalCorners.length;
  const centerY =
    originalCorners.reduce((sum, [, y]) => sum + y, 0) / originalCorners.length;

  // Derive local axes from the original box so we can preserve its
  // rotation. Use the edges from corner 0 to its two neighbours.
  const p0 = originalCorners[0];
  const p1 = originalCorners[1];
  const p3 = originalCorners[3];

  const edgeUx = p1[0] - p0[0];
  const edgeUy = p1[1] - p0[1];
  const edgeVx = p3[0] - p0[0];
  const edgeVy = p3[1] - p0[1];

  const edgeUlength = Math.hypot(edgeUx, edgeUy);
  const edgeVlenght = Math.hypot(edgeVx, edgeVy);

  if (!edgeUlength || !edgeVlenght) {
    // if either egde has zero length, bail out
    return originalGeometry;
  }

  const ux = edgeUx / edgeUlength;
  const uy = edgeUy / edgeUlength;
  const vx = edgeVx / edgeVlenght;
  const vy = edgeVy / edgeVlenght;

  // Remember which corner is which
  const cornerSigns = [
    { sx: -1, sy: -1 },
    { sx: 1, sy: -1 },
    { sx: 1, sy: 1 },
    { sx: -1, sy: 1 },
  ];

  const minSize = 1e-6;

  // Squares keep their center fixed and scale uniformly from the
  // dragged corner.
  if (keepSquare) {
    // Project the dragged corner into the same local coordinate
    // system to determine the new half-size while keeping the
    // existing rotation.
    const drx = dragged[0] - centerX;
    const dry = dragged[1] - centerY;
    const dragLocalX = drx * ux + dry * uy;
    const dragLocalY = drx * vx + dry * vy;

    const halfWidth = Math.max(Math.abs(dragLocalX), minSize);
    const halfHeight = Math.max(Math.abs(dragLocalY), minSize);
    const halfSize = Math.max(halfWidth, halfHeight);

    const newCorners: Coordinate[] = originalCorners.map((_, idx) => {
      const { sx, sy } = cornerSigns[idx];
      const localX = sx * halfSize;
      const localY = sy * halfSize;

      const worldX = centerX + localX * ux + localY * vx;
      const worldY = centerY + localX * uy + localY * vy;
      return [worldX, worldY];
    });

    newCorners.push(newCorners[0]);
    return new Polygon([newCorners]);
  }

  // Rectangles (non-squares) should resize using the dragged
  // corner while keeping the diagonal opposite anchored. The two remaining
  // corners adjust accordingly, and the overall rotation is kept. When the
  // dragged corner crosses past the center, the rectangle is allowed to
  // "flip" instead of bouncing back.

  // Diagonal corner relative to the dragged one in the 4-corner ring
  const anchorIndex = (draggedCornerIndex + 2) % originalCorners.length;
  const anchor = originalCorners[anchorIndex];

  // Vector from anchored corner to dragged corner, in world space
  const deltaWx = dragged[0] - anchor[0];
  const deltaWy = dragged[1] - anchor[1];

  // Express that vector in the local (u, v) frame
  const deltaLocalX = deltaWx * ux + deltaWy * uy;
  const deltaLocalY = deltaWx * vx + deltaWy * vy;

  // Half-sizes in local coordinates. We keep the sign so that when the
  // dragged corner crosses past the anchor along either axis, the
  // rectangle flips instead of being clamped.
  const halfWidth = deltaLocalX / 2;
  const halfHeight = deltaLocalY / 2;

  // Center in local coordinates is halfway between anchor (0,0) and
  // dragged (deltaLocalX, deltaLocalY).
  const centerLocalX = halfWidth;
  const centerLocalY = halfHeight;

  // Convert center back to world coordinates.
  const centerXNew = anchor[0] + centerLocalX * ux + centerLocalY * vx;
  const centerYNew = anchor[1] + centerLocalX * uy + centerLocalY * vy;

  // Rebuild the four corners in local coordinates around the new center.
  const localCorners: Coordinate[] = [
    [-halfWidth, -halfHeight],
    [halfWidth, -halfHeight],
    [halfWidth, halfHeight],
    [-halfWidth, halfHeight],
  ];

  const newCornersWorld: Coordinate[] = localCorners.map(([lx, ly]) => {
    const worldX = centerXNew + lx * ux + ly * vx;
    const worldY = centerYNew + lx * uy + ly * vy;
    return [worldX, worldY];
  });

  // Normalise corner order so index 0..3 always correspond to
  // bottom-left, bottom-right, top-right, top-left in the box's
  // local frame. This keeps rotate-handle orientation and
  // indexing stable even after the rectangle "flips" when a
  // corner is dragged past the centre.
  const classified = newCornersWorld.map((corner) => {
    const [wx, wy] = corner;
    const rx = wx - centerXNew;
    const ry = wy - centerYNew;
    const localX = rx * ux + ry * uy;
    const localY = rx * vx + ry * vy;
    return {
      corner,
      qx: localX >= 0 ? 1 : -1,
      qy: localY >= 0 ? 1 : -1,
    };
  });

  const bl = classified.find(
    (corner) => corner.qx === -1 && corner.qy === -1,
  )?.corner;
  const br = classified.find(
    (corner) => corner.qx === 1 && corner.qy === -1,
  )?.corner;
  const tr = classified.find(
    (corner) => corner.qx === 1 && corner.qy === 1,
  )?.corner;
  const tl = classified.find(
    (corner) => corner.qx === -1 && corner.qy === 1,
  )?.corner;

  const orderedCorners: Coordinate[] =
    bl && br && tr && tl ? [bl, br, tr, tl] : newCornersWorld;

  // Close the ring
  orderedCorners.push(orderedCorners[0]);

  return new Polygon([orderedCorners]);
};

export const getBoxRotateHandlePoints = (
  feature: FeatureLike,
  resolution?: number,
): Coordinate[] | undefined => {
  const originalGeometry = feature?.get('originalGeometry') as Polygon;
  const geom = originalGeometry
    ? createBoxBasedOnModifiedVertex(
        feature.getGeometry() as Polygon,
        originalGeometry,
        feature.get('selectionType') === 'square',
      )
    : feature?.getGeometry();

  if (!geom || geom?.getType() !== 'Polygon') {
    return undefined;
  }

  const ring = (geom as Polygon).getCoordinates()[0];
  if (ring.length < 4) {
    return undefined;
  }

  // First four coordinates are the corners of the box (possibly rotated)
  const corners = ring.slice(0, 4);

  // Compute box center as the average of the corners
  const centerX = corners.reduce((sum, [x]) => sum + x, 0) / corners.length;
  const centerY = corners.reduce((sum, [, y]) => sum + y, 0) / corners.length;

  // Place the rotate handle at a nearly constant distance from the
  // corner in screen space, independent of how large the box is.
  // We move a fixed number of pixels along the outward diagonal
  // from each corner.
  const pixelOffset = 16; // pixels away from the corner
  const baseOffset = resolution ? pixelOffset * resolution : 0;

  return corners.map(([x, y]) => {
    const dx = x - centerX;
    const dy = y - centerY;
    const distance = Math.hypot(dx, dy);

    if (!distance || !baseOffset) {
      return [x, y] as Coordinate;
    }

    const ux = dx / distance;
    const uy = dy / distance;

    return [x + ux * baseOffset, y + uy * baseOffset] as Coordinate;
  });
};

const boxModificationStyle: StyleLike = (
  feature: FeatureLike,
  resolution: number,
): Style[] => {
  const styles: Style[] = [];

  const originalGeometry = feature?.get('originalGeometry') as Polygon;
  const geom = originalGeometry
    ? createBoxBasedOnModifiedVertex(
        feature.getGeometry() as Polygon,
        originalGeometry,
        feature.get('selectionType') === 'square',
      )
    : (feature.getGeometry() as Geometry | undefined);

  if (geom) {
    styles.push(
      new Style({
        fill: FEATURE_FILL,
        stroke: FEATURE_STROKE_EDIT,
        geometry: geom,
      }),
    );
  }

  if (
    geom &&
    (geom.getType() === 'Polygon' || geom.getType() === 'LineString')
  ) {
    const coordinates =
      geom.getType() === 'Polygon'
        ? (geom as Polygon).getCoordinates()[0]
        : (geom as LineString).getCoordinates();
    styles.push(
      new Style({
        image: FEATURE_VERTICE_IMAGE,
        geometry: new MultiPoint(coordinates),
      }),
    );
  }

  const handlePoints = getBoxRotateHandlePoints(feature, resolution);

  const activeIndexRaw = feature.get('activeRotateHandleIndex');
  const activeIndex =
    typeof activeIndexRaw === 'number' &&
    activeIndexRaw >= 0 &&
    activeIndexRaw < 4
      ? activeIndexRaw
      : undefined;

  // While the box is being modified (corner drag), hide the rotate
  // handle so only sizing handles are visible.
  const suppressRotateHandle = feature.get('suppressRotateHandle');

  if (
    ROTATE_ICON_CANVAS &&
    handlePoints &&
    handlePoints.length >= 4 &&
    activeIndex !== undefined &&
    !suppressRotateHandle
  ) {
    const [h0, h1, h2, h3] = handlePoints;

    // Base angle: along the edge between first two corner handles.
    const baseAngle = Math.atan2(h1[1] - h0[1], h1[0] - h0[0]);

    // Assign per-corner offsets.
    const handleConfigs = [
      { point: h0, offset: 0 }, // bottom-left
      { point: h1, offset: -Math.PI / 2 }, // bottom-right
      { point: h2, offset: Math.PI }, // top-right
      { point: h3, offset: Math.PI / 2 }, // top-left
    ];

    const { point, offset } = handleConfigs[activeIndex];

    const icon = new Icon({
      img: ROTATE_ICON_CANVAS,
      rotation: -baseAngle + offset,
    });

    styles.push(
      new Style({
        image: icon,
        geometry: new MultiPoint([point]),
      }),
    );
  }

  return styles;
};

const circleFeatureStyle = (
  feature: FeatureLike,
  resolution: number,
): Style[] => {
  // use OL default styles to keep circle edge tracking
  const styles = createDefaultStyle(feature, resolution);

  // modify stroke + fill
  styles.forEach((style) => {
    if (style.getStroke()) {
      style.setStroke(FEATURE_STROKE_EDIT);
    }
    if (style.getFill()) {
      style.setFill(FEATURE_FILL);
    }
  });

  return styles;
};

// Create an ellipse geometry from center + two drag points
const createEllipsePolygon = (center: number[], last: number[]): Polygon => {
  // Radii along x and y
  const rx = Math.abs(last[0] - center[0]);
  const ry = Math.abs(last[1] - center[1]);

  const points = [];
  const steps = 64; // smoothness

  for (let i = 0; i < steps; i += 1) {
    const theta = (i / steps) * 2 * Math.PI;
    points.push([
      center[0] + rx * Math.cos(theta),
      center[1] + ry * Math.sin(theta),
    ]);
  }

  // Ensure the ring is closed
  if (points.length > 0) {
    points.push(points[0]);
  }

  return new Polygon([points]);
};

export const createOval = (): GeometryFunction => {
  return (
    coordinates: SketchCoordType,
    geometry: SimpleGeometry,
  ): SimpleGeometry => {
    const center = coordinates[0] as number[];
    const last = coordinates[1] as number[];
    const newGeometry: SimpleGeometry = geometry || new Polygon([]);

    newGeometry.setCoordinates(
      createEllipsePolygon(center, last).getCoordinates(),
    );

    return newGeometry;
  };
};

export const drawStyles: Record<DrawModeValue, StyleLike> = {
  POLYGON: drawPolygonLikeFeatureStyle,
  BOX: drawPolygonLikeFeatureStyle,
  SQUARE: drawPolygonLikeFeatureStyle,
  LINESTRING: drawPolygonLikeFeatureStyle,
  FREEHANDLINE: freehandLineFeatureStyle,
  SMOOTHLINE: smoothLineLikeFeatureStyle,
  CIRCLE: circleFeatureStyle,
  OVAL: circleFeatureStyle,
  POINT: pointFeatureStyle,
  MULTIPOINT: pointFeatureStyle,
  LOCATION: locationFeatureStyle,
  ROTATABLEBOX: drawPolygonLikeFeatureStyle,
  DELETE: [],
  '': [],
};

export const modifyStyles: Record<DrawModeValue, Style[] | StyleLike> = {
  POLYGON: modifyPolygonLikeFeatureStyle,
  BOX: boxModificationStyle,
  SQUARE: boxModificationStyle,
  LINESTRING: modifyPolygonLikeFeatureStyle,
  FREEHANDLINE: freehandLineFeatureStyle,
  SMOOTHLINE: smoothLineLikeFeatureStyle,
  CIRCLE: circleFeatureStyle,
  OVAL: circleFeatureStyle,
  POINT: modifyPointFeatureStyle,
  MULTIPOINT: modifyPointFeatureStyle,
  LOCATION: locationFeatureStyle,
  ROTATABLEBOX: boxModificationStyle,
  DELETE: [],
  '': [],
};
